import React, { useEffect, useState } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { IoAddCircleOutline, IoClose, IoGiftOutline } from "react-icons/io5";
import Button from '../components/Button';
import Services from '../services/Services';
import { Box, Modal, CircularProgress } from '@mui/material';
import { getOrCreateSessionID } from '../utils/sessionManager';
import { logEvent } from '../utils/analyticsManager'; // GA4 Import

export default function CompanyCampaigns() {
  const { token, companyID } = useParams();
  const location = useLocation();
  const { state } = location;
  const { company } = state;
  const navigate = useNavigate();

  const [companyCampaigns, setCompanyCampaigns] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Mevcut bilgilendirme modalı
  const [openModal, setOpenModal] = useState(false);

  // Kullanıcının EasyPoint ID'si (poiID)
  const [poiID, setPoiID] = useState("");

  // Kupon durumu ve içerik
  const [hasCoupon, setHasCoupon] = useState(null);
  const [hadCoupon, setHadCoupon] = useState(null);
  const [couponModalOpen, setCouponModalOpen] = useState(false);
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponData, setCouponData] = useState(null);
  const [couponError, setCouponError] = useState("");
  const [campaignCoupons, setCampaignCoupons] = useState({});

  // SessionID için state
  const [sessionID, setSessionID] = useState(null);

  const handleCampaignClick = (campaignID, campaignTitle) => {
    // GA4: Kampanya detayı tıklama
    logEvent('Campaign', 'Kampanya Detayına Tıklandı', `${company.title} - ${campaignTitle}`);

    // Mevcut Backend Tracking
    if (sessionID) {
      Services.AddAnalyticsTracking(
        token,
        sessionID,
        'campaign_button_click',
        `${company.title} - ${campaignTitle}`,
        'click'
      ).catch((err) => console.log('Analytics tracking error:', err));
    }

    navigate(`/${token}/${companyID}/${campaignID}`);
  };

  const checkCampaignHasCoupon = async (cid) => {
    try {
      const res = await Services.GetBolParaCampaignHasCoupon(token, cid);
      const ok = res?.data?.status === true;
      const result = res?.data?.result;

      if (!ok) return;

      if (result && typeof result === "object") {
        setCampaignCoupons((prev) => ({
          ...prev,
          [cid]: { hasCoupon: Boolean(result.hasCoupon), hadCoupon: Boolean(result.hadCoupon) },
        }));
      } else {
        setCampaignCoupons((prev) => ({
          ...prev,
          [cid]: { hasCoupon: true, hadCoupon: true },
        }));
      }
    } catch (_) {
    }
  };

  const getCompanyCampaigns = () => {
    Services.GetBolParaCompanyCampaigns(token, companyID)
      .then((res) => {
        if (res.data.status) {
          const campaigns = res.data.result || [];
          const today = Math.floor(Date.now() / 1000);
          const filteredCampaigns = campaigns.filter(campaign => {
            const startDate = parseInt(campaign.start);
            const endDate = parseInt(campaign.end);
            return today >= startDate && today <= endDate;
          });
          console.log("Kampanyalar:", filteredCampaigns)
          setCompanyCampaigns(filteredCampaigns);
        } else {
          setCompanyCampaigns([]);
        }
        setIsLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setIsLoading(false);
      });
  };

  const getPoiID = () => {
    Services.BolParaAboutToken(token)
      .then((res) => {
        if (res.data?.status) {
          setPoiID(res.data.result?.easypointID || "");
        }
      })
      .catch((err) => console.log(err));
  };

  // Şirket için kupon var mı kontrolü
  const checkCompanyHasCoupon = () => {
    setHasCoupon(null);
    setHadCoupon(null);

    Services.GetBolParaCompanyHasCoupon(token, companyID)
      .then((res) => {
        const status = res?.data?.status === true;

        if (!status) {
          setHasCoupon(false);
          setHadCoupon(false);
          return;
        }

        const result = res?.data?.result;

        if (result && typeof result === "object") {
          // { hasCoupon: boolean, hadCoupon: boolean }
          const hc = Boolean(result.hasCoupon);
          const hd = Boolean(result.hadCoupon);
          setHasCoupon(hc);
          setHadCoupon(hd);
        } else {
          // result YOKSA: "kupon girildi ve VAR" anlamı → buton aktif
          setHasCoupon(true);
          setHadCoupon(true);
        }
      })
      .catch(() => {
        // Hata durumunda güvenli varsayım: buton göstermeyelim
        setHasCoupon(false);
        setHadCoupon(false);
      });
  };

  useEffect(() => {
    // SessionID'yi al (MainPage'de oluşturulmuş olmalı)
    const sid = getOrCreateSessionID();
    setSessionID(sid);

    getCompanyCampaigns();
    getPoiID();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Kampanyalarla paralel olarak kupon durumunu da çekelim
    checkCompanyHasCoupon();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyID, token]);

  const userOfficeId = poiID;

  const filteredCampaigns = companyCampaigns.filter(campaign => {
    const points = Array.isArray(campaign.allowedPoints) ? campaign.allowedPoints : [];
    return points.includes(userOfficeId);
  });

  useEffect(() => {
    if (filteredCampaigns.length) {
      filteredCampaigns.forEach((c) => {
        if (!campaignCoupons[c.campaignID]) {
          checkCampaignHasCoupon(c.campaignID);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredCampaigns, token]);


  // Kupon talep etme
  const handleGetCouponClick = () => {
    // GA4: Kupon al buton tıklama
    logEvent('Coupon', 'Kupon Al Tıklandı', `${company.title}`);

    // Mevcut Backend Tracking
    if (sessionID) {
      Services.AddAnalyticsTracking(
        token,
        sessionID,
        'coupon_button_click',
        `${company.title} - Kupon Al`,
        'click'
      ).catch((err) => console.log('Analytics tracking error:', err));
    }

    setCouponError("");
    setCouponLoading(true);
    setCouponData(null);

    Services.GetNewCompanyCoupon(token, companyID, poiID)
      .then((res) => {
        if (res?.data?.status) {
          setCouponData(res.data.result || res.data);
          setCouponModalOpen(true);

          // 🔁 Kupon alındıktan sonra yeniden kontrol et
          checkCompanyHasCoupon();
        } else {
          setCouponError("Kupon alınamadı. Lütfen tekrar deneyin.");
          setCouponModalOpen(true);
        }
      })
      .catch((err) => {
        console.log(err);
        setCouponError("Beklenmeyen bir hata oluştu. Lütfen tekrar deneyin.");
        setCouponModalOpen(true);
      })
      .finally(() => setCouponLoading(false));
  };

  // Kupon içeriğini esnek biçimde renderla
  const renderCouponContent = () => {
    if (couponLoading) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
          <CircularProgress />
        </div>
      );
    }
    if (couponError) {
      return <p style={{ color: '#e11d48', fontSize: 14 }}>{couponError}</p>;
    }
    if (!couponData) {
      return <p style={{ color: '#64748B', fontSize: 12 }}>Gösterilecek kupon bulunamadı.</p>;
    }

    // Yaygın alanlar: code, title, description, expireDate, qrUrl
    const code = couponData.code || couponData.couponCode || couponData.CouponCode;
    const title = couponData.title || couponData.campaignTitle || company?.title + " Kuponu";
    const description = couponData.description || couponData.desc;
    const expireDate = couponData.expireDate || couponData.expiration || couponData.validUntil;
    const qrUrl = couponData.qrUrl || couponData.qrCodeUrl || couponData.qr;

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <h3 style={{ margin: 0 }}>{title}</h3>
        {description && <p style={{ color: '#475569', margin: 0 }}>{description}</p>}
        {code && (
          <div style={{
            background: '#f1f5f9',
            border: '1px dashed #94a3b8',
            padding: '12px 16px',
            borderRadius: 8,
            fontWeight: 700,
            letterSpacing: 1.2,
            textAlign: 'center'
          }}>
            {code}
          </div>
        )}
        {expireDate && (
          <p style={{ color: '#64748B', fontSize: 12, margin: 0 }}>
            Son Kullanım: {expireDate}
          </p>
        )}
        {qrUrl && (
          <img
            src={qrUrl}
            alt="Kupon QR Kodu"
            style={{ alignSelf: 'center', maxWidth: 220, height: 'auto' }}
          />
        )}
        {/* Tamamen bilinmeyen alanlar için yedek: */}
        {!code && !description && !qrUrl && (
          <pre style={{
            background: '#0f172a',
            color: '#e2e8f0',
            padding: 12,
            borderRadius: 8,
            maxHeight: 300,
            overflow: 'auto'
          }}>
            {JSON.stringify(couponData, null, 2)}
          </pre>
        )}
      </div>
    );
  };

  // Modal stilleri
  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '70%',
    backgroundColor: 'white',
    padding: '20px',
    borderRadius: '8px',
    boxShadow: 24,
  };

  const couponModalStyle = {
    ...modalStyle,
    width: '520px',
    maxWidth: '92vw'
  };

  return (
    <body>
      <div className='main'>
        {isLoading ? (
          <div style={{ marginTop: '35%' }}>
            <CircularProgress />
          </div>
        ) : (
          <div style={{ height: '100vh' }}>
            <img
              src={company.logo}
              alt={`${company.title} logo`}
              style={{
                marginTop: '15%',
                maxWidth: '100%',
                height: 'auto',
                objectFit: 'contain',
              }}
            />

            {company.title !== "Avon" ? (
              <>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    flexDirection: 'row',
                    alignSelf: 'flex-start',
                  }}
                >
                  <p style={{ color: '#494D59', fontWeight: '700', fontSize: '18px' }}>
                    {company.title} Paket Satışı Nasıl Yapılır?
                  </p>
                </div>
                <p
                  onClick={() => setOpenModal(true)}
                  style={{
                    color: '#64748B',
                    fontWeight: '400',
                    fontSize: '12px',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    alignSelf: 'flex-start',
                    marginTop: '10px',
                  }}
                >
                  Detayları görmek için tıklayın
                </p>
              </>
            ) : (
              <p style={{
                color: '#64748B',
                fontWeight: '400',
                fontSize: '12px',
                alignSelf: 'flex-start',
                marginTop: '20px',
              }}>
                Selam sevgili esnaf dostum! Sana hem kazandıracak hem de mağazana renk katacak yepyeni bir fırsatımız var: Easy Point ile Avon markasını mağazanıza getiriyoruz! Hem müşterilerin memnun olacak hem senin ciron artacak. Nasıl mı? Alt kısımlarda yer alan işbirliği butonlarına tıklaman yeterli!
              </p>
            )}



            {/* Kampanya butonları */}
            <div style={{ display: 'flex', flexDirection: 'column', width: '100%', height: '60%', overflowY: 'auto' }}>

              {company?.brandBasedGetCouponButtonVisibleStatus === "1" &&
                <>
                  {hasCoupon === null ? (
                    // Yükleniyor
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                      <CircularProgress size={18} />
                      <span style={{ fontSize: 12, color: '#64748B' }}>Kupon uygunluğu kontrol ediliyor…</span>
                    </div>
                  ) : hasCoupon === true ? (
                    // Kupon var → buton aktif
                    <div style={{ marginTop: 16 }}>
                      <Button
                        style={{ width: '100%' }}
                        onClick={handleGetCouponClick}
                        label={couponLoading ? 'Kupon alınıyor...' : 'Kupon Al'}
                        icon={<IoGiftOutline color='white' size={22} />}
                        disabled={couponLoading}
                      />
                    </div>
                  ) : hadCoupon === true ? (
                    // Daha önce kupon girilmiş ama bitmiş → buton disabled + not
                    <div style={{ marginTop: 16 }}>
                      <Button
                        style={{ width: '100%', textAlign: 'left' }}
                        onClick={handleGetCouponClick}
                        label="Kupon Al"
                        disabledNote={"(İndirim Kodu Mevcut Değil. En Kısa Sürede Eklenecektir.)"}
                        icon={<IoGiftOutline color='white' size={22} />}
                        disabled
                      />

                    </div>
                  ) : null}
                </>
              }


              {filteredCampaigns.map((campaign) => {
                const cState = campaignCoupons[campaign.campaignID] || {};
                const isDepleted = cState.hadCoupon === true && cState.hasCoupon === false;

                return (
                  <div key={campaign.campaignID} style={{ marginTop: 25 }}>
                    <Button
                      style={{ width: '100%', boxSizing: 'border-box', textAlign: 'left' }}
                      onClick={() => handleCampaignClick(campaign.campaignID, campaign.title)}
                      label={campaign.title}
                      disabledNote={"(İndirim Kodu Mevcut Değil. En Kısa Sürede Eklenecektir.)"}
                      icon={<IoAddCircleOutline color="white" size={24} style={{ minWidth: '24px' }} />}
                      disabled={isDepleted}
                    />

                  </div>
                );
              })}
            </div>

            {/* Bilgilendirme Modalı */}
            <Modal
              open={openModal}
              onClose={() => setOpenModal(false)}
              aria-labelledby="modal-title"
              aria-describedby="modal-description"
            >
              <Box sx={modalStyle}>
                <button
                  onClick={() => setOpenModal(false)}
                  style={{
                    position: 'absolute',
                    top: '8px',
                    right: '8px',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '1.5rem',
                    padding: 0,
                  }}
                  aria-label="Kapat"
                >
                  <IoClose />
                </button>
                <ol
                  style={{
                    color: '#64748B',
                    fontWeight: '400',
                    fontSize: '12px',
                    paddingLeft: '20px',
                    marginTop: '40px',
                  }}
                >
                  <li>
                    {company.title} Paket Seçin: Müşterilerinize sunmak için mevcut {company.title} paketlerinden birini seçin.
                  </li>
                  <li>
                    Paket Detaylarını Paylaşın: Seçtiğiniz paketlerin içeriklerini ve fiyatlarını müşterinize detaylı bir şekilde aktarın.
                  </li>
                  <li>
                    Satışa Yönlendirin: Satışı tamamlamak için "QR Kodu Görüntüle" butonuna basın ve müşteriden kendi telefonundan QR kodunu okutup gerekli bilgileri tamamlamasını isteyin. Ardından "Hemen Başvur" butonuna basmasını söyleyin.
                  </li>
                  <li>
                    Satışın Tamamlanması: Müşterinize, 48 saat içinde {company.title} merkezinin işlemleri tamamlamak için kendisini arayacağı bilgisini iletin.
                  </li>
                  <li>
                    Satışın Onaylanması: Eğer ilgili ekiplerinin araması sonucu satış olumlu olursa, kazanç sağlarsınız.
                  </li>
                  <li>
                    Komisyon Kazanın: Her satıştan kazandığınız komisyonu Easy Point uygulamasında takip edebilirsiniz.
                  </li>
                </ol>
              </Box>
            </Modal>

            {/* Kupon Modalı */}
            <Modal
              open={couponModalOpen}
              onClose={() => setCouponModalOpen(false)}
              aria-labelledby="coupon-modal-title"
              aria-describedby="coupon-modal-description"
            >
              <Box sx={couponModalStyle}>
                <button
                  onClick={() => setCouponModalOpen(false)}
                  style={{
                    position: 'absolute',
                    top: '8px',
                    right: '8px',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '1.5rem',
                    padding: 0,
                  }}
                  aria-label="Kapat"
                >
                  <IoClose />
                </button>
                <div style={{ marginTop: 24 }}>
                  {renderCouponContent()}
                </div>
              </Box>
            </Modal>
          </div>
        )}
      </div>
    </body>
  );
}